import React from 'react';
import { Typography, Button, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleIcon from '@mui/icons-material/Article';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';

import { identity } from '../../config/site';
import { Project, Article, LifeMetric, SiteControls, SocialChannel, AboutData } from '../../types/content';
import { seedSocialChannels } from '../../data/seed';

interface BentoDashboardProps {
  projects: Project[];
  articles: Article[];
  metrics: LifeMetric[];
  controls: SiteControls;
  visitorCount: number | null;
  channels?: SocialChannel[];
  about?: AboutData;
}

export const BentoDashboard: React.FC<BentoDashboardProps> = ({
  projects,
  articles,
  metrics,
  controls,
  visitorCount,
  channels,
  about,
}) => {
  const channelList: SocialChannel[] = channels && channels.length > 0 ? channels : seedSocialChannels;
  const displayChannels: SocialChannel[] = channelList.filter((c: SocialChannel) => c.visible !== false);
  const displayAbout = about || {
    handle: identity.handle,
    avatarText: identity.avatarText,
    bio: identity.bio,
    status: { indicator: 'active', label: 'ACTIVE', sublabel: 'Personal hub & telemetry outside work' },
    location: { label: 'India', visible: true },
    controls,
    hobbies: [],
    passions: [],
  };

  const pinnedProjects = projects.filter((p) => p.pinned && p.visible).slice(0, 3);
  const recentArticles = articles.filter((a) => a.published).slice(0, 2);

  // Group metrics by month if available
  const latestMonth = metrics.length > 0 ? metrics[0].month : null;
  const monthMetrics = latestMonth
    ? metrics.filter((m) => m.month === latestMonth && m.visible)
    : [];

  const getMetricValue = (key: string) => {
    const found = monthMetrics.find((m) => m.metric === key);
    return found ? { value: found.value, unit: found.unit } : null;
  };

  const stepsMetric = getMetricValue('steps');
  const bikeMetric = getMetricValue('bike_hours');
  const gymMetric = getMetricValue('gym_hours');
  const sleepMetric = getMetricValue('sleep_avg_hours');

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active':
        return { text: '#38BDF8', bg: 'rgba(56, 189, 248, 0.1)', border: 'rgba(56, 189, 248, 0.25)' };
      case 'shipped':
        return { text: '#4ADE80', bg: 'rgba(74, 222, 128, 0.1)', border: 'rgba(74, 222, 128, 0.25)' };
      case 'idea':
        return { text: '#FACC15', bg: 'rgba(250, 204, 21, 0.1)', border: 'rgba(250, 204, 21, 0.25)' };
      default:
        return { text: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.25)' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── ROW 1: Identity Card ── */}
      <section
        style={{
          borderRadius: 20,
          backgroundColor: 'rgba(20, 20, 22, 0.65)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(239, 196, 48, 0.18)',
          padding: '28px 32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle ambient light */}
        <div
          style={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(239, 196, 48, 0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #EFC430 0%, #C99A16 100%)',
                color: '#0A0A0A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '1.3rem',
                boxShadow: '0 4px 14px rgba(239, 196, 48, 0.25)',
              }}
            >
              {displayAbout.avatarText}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}
                >
                  {displayAbout.handle}
                </Typography>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    backgroundColor: 'rgba(34, 197, 94, 0.12)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    padding: '3px 8px',
                    borderRadius: 14,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#22C55E',
                      boxShadow: '0 0 6px #22C55E',
                    }}
                  />
                  <span style={{ color: '#86EFAC', fontSize: '0.7rem', fontWeight: 600 }}>ACTIVE</span>
                </div>
              </div>

              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.85rem', mt: 0.3 }}>
                Personal hub & telemetry outside work
              </Typography>
            </div>
          </div>

          {/* Right badges: Location & Visitor counter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {controls.show_location && controls.location_label && (
              <Chip
                icon={<LocationOnIcon style={{ color: '#EFC430', fontSize: 14 }} />}
                label={controls.location_label}
                size="small"
                sx={{
                  backgroundColor: 'rgba(239, 196, 48, 0.08)',
                  border: '1px solid rgba(239, 196, 48, 0.25)',
                  color: '#FFFFFF',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              />
            )}

            {controls.show_visitor_count && visitorCount !== null && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  borderRadius: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  fontSize: '0.75rem',
                  color: '#9CA3AF',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 13, color: '#EFC430' }} />
                <span>{visitorCount.toLocaleString()} visits</span>
              </div>
            )}
          </div>
        </div>

        <Typography
          variant="body1"
          sx={{ color: '#D1D5DB', fontSize: '0.94rem', lineHeight: 1.65, maxWidth: 760 }}
        >
          {displayAbout.bio}
        </Typography>
      </section>

      {/* ── ROW 2: Life Pulse Telemetry ── */}
      {controls.show_life_pulse && (
        <section
          style={{
            borderRadius: 20,
            backgroundColor: 'rgba(20, 20, 22, 0.65)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            padding: '24px 28px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.2rem' }}>⚡</span>
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.1rem' }}>
                Life Pulse & Activity Telemetry
              </Typography>
            </div>

            {latestMonth && (
              <span
                style={{
                  color: '#EFC430',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(239, 196, 48, 0.08)',
                  padding: '3px 10px',
                  borderRadius: 8,
                }}
              >
                {latestMonth}
              </span>
            )}
          </div>

          {monthMetrics.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 14,
              }}
            >
              {stepsMetric && (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: 14,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#EFC430', marginBottom: 6 }}>
                    <DirectionsWalkIcon sx={{ fontSize: 18 }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Steps Walked</span>
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#FFFFFF' }}>
                    {stepsMetric.value.toLocaleString()}
                  </div>
                </div>
              )}

              {bikeMetric && (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: 14,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#38BDF8', marginBottom: 6 }}>
                    <DirectionsBikeIcon sx={{ fontSize: 18 }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Cycling / Saddle</span>
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#FFFFFF' }}>
                    {bikeMetric.value} {bikeMetric.unit}
                  </div>
                </div>
              )}

              {gymMetric && (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: 14,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#F43F5E', marginBottom: 6 }}>
                    <FitnessCenterIcon sx={{ fontSize: 18 }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Gym / Strength</span>
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#FFFFFF' }}>
                    {gymMetric.value} {gymMetric.unit}
                  </div>
                </div>
              )}

              {sleepMetric && (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: 14,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A855F7', marginBottom: 6 }}>
                    <BedtimeIcon sx={{ fontSize: 18 }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Sleep Recovery</span>
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#FFFFFF' }}>
                    {sleepMetric.value} {sleepMetric.unit}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                padding: '24px',
                borderRadius: 14,
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px dashed rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: '0.88rem',
                lineHeight: 1.6,
              }}
            >
              Monthly physical telemetry (steps, gym hours, saddle time, sleep average) is compiled and published
              on a 30-day delay for personal privacy. Telemetry batches will appear here as past months close.
            </div>
          )}
        </section>
      )}

      {/* ── ROW 3: Project Spotlight (Pinned Real Projects) & Recent Activity ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 20,
        }}
      >
        {/* TILE: Project Spotlight */}
        <section
          style={{
            borderRadius: 20,
            backgroundColor: 'rgba(20, 20, 22, 0.65)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            padding: '26px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.2rem' }}>🔨</span>
                <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.1rem' }}>
                  Project Spotlight
                </Typography>
              </div>

              <Button
                component={RouterLink}
                to="/projects"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
                sx={{
                  color: '#EFC430',
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  p: 0,
                  '&:hover': { backgroundColor: 'transparent', color: '#F4D675' },
                }}
              >
                All Projects
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pinnedProjects.map((proj) => {
                const statusStyle = getStatusColor(proj.status);
                return (
                  <div
                    key={proj.slug}
                    style={{
                      padding: '14px 16px',
                      borderRadius: 14,
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <StarOutlineIcon sx={{ fontSize: 14, color: '#EFC430' }} />
                        <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.95rem' }}>
                          {proj.title}
                        </Typography>
                      </div>

                      <span
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          color: statusStyle.text,
                          backgroundColor: statusStyle.bg,
                          border: `1px solid ${statusStyle.border}`,
                          padding: '2px 8px',
                          borderRadius: 6,
                          textTransform: 'uppercase',
                        }}
                      >
                        {proj.status}
                      </span>
                    </div>

                    <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.82rem', lineHeight: 1.5, my: 0.6 }}>
                      {proj.tagline}
                    </Typography>

                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                      {proj.tags.slice(0, 4).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            color: '#D1D5DB',
                            fontSize: '0.68rem',
                            height: 20,
                          }}
                        />
                      ))}
                    </div>

                    {proj.links.actions && proj.links.actions.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        {proj.links.actions.map((act) => (
                          <a
                            key={act.url}
                            href={act.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: '0.72rem',
                              color: '#38BDF8',
                              backgroundColor: 'rgba(56, 189, 248, 0.08)',
                              border: '1px solid rgba(56, 189, 248, 0.25)',
                              padding: '2px 8px',
                              borderRadius: 6,
                              textDecoration: 'none',
                              fontWeight: 600,
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <span>{act.label}</span>
                            <span style={{ fontSize: '0.65rem' }}>↗</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TILE: Recent Activity & Writing */}
        <section
          style={{
            borderRadius: 20,
            backgroundColor: 'rgba(20, 20, 22, 0.65)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            padding: '26px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.2rem' }}>📝</span>
                <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.1rem' }}>
                  Recent Writing & Notes
                </Typography>
              </div>

              <Button
                component={RouterLink}
                to="/blogs"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
                sx={{
                  color: '#EFC430',
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  p: 0,
                  '&:hover': { backgroundColor: 'transparent', color: '#F4D675' },
                }}
              >
                All Notes
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {recentArticles.map((art) => (
                <div
                  key={art.slug}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 14,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#EFC430', fontSize: '0.72rem', fontWeight: 600 }}>
                    <ArticleIcon sx={{ fontSize: 14 }} />
                    <span style={{ textTransform: 'uppercase' }}>{art.format}</span>
                    {art.read_time && <span>• {art.read_time}</span>}
                  </div>

                  <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.92rem', mt: 0.5 }}>
                    {art.title}
                  </Typography>

                  {art.excerpt && (
                    <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.8rem', mt: 0.5, lineHeight: 1.5 }}>
                      {art.excerpt}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ── ROW 4: Life Graph Channels Strip ── */}
      <section
        style={{
          borderRadius: 20,
          backgroundColor: 'rgba(20, 20, 22, 0.65)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.2rem' }}>🌐</span>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem' }}>
              The Life Graph: Multiple Perspectives
            </Typography>
          </div>

          <Button
            component={RouterLink}
            to="/links"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
            sx={{
              color: '#EFC430',
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 600,
              p: 0,
              '&:hover': { backgroundColor: 'transparent', color: '#F4D675' },
            }}
          >
            Full Life Graph
          </Button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
          }}
        >
          {displayChannels.map((channel) => (
            <a
              key={channel.id}
              href={channel.url}
              target={channel.url.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                padding: '12px 14px',
                borderRadius: 12,
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'border-color 0.2s ease, transform 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {channel.icon === 'instagram' && <InstagramIcon sx={{ fontSize: 20, color: channel.accent }} />}
                {channel.icon === 'github' && <GitHubIcon sx={{ fontSize: 20, color: channel.accent }} />}
                {channel.icon === 'linkedin' && <LinkedInIcon sx={{ fontSize: 20, color: channel.accent }} />}
                {channel.icon === 'twitter' && <TwitterIcon sx={{ fontSize: 20, color: channel.accent }} />}
                {channel.icon === 'email' && <EmailIcon sx={{ fontSize: 20, color: channel.accent }} />}

                <div>
                  <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.85rem' }}>{channel.platform}</div>
                  <div style={{ color: '#9CA3AF', fontSize: '0.72rem' }}>{channel.context}</div>
                </div>
              </div>

              <span style={{ color: channel.accent, fontSize: '0.8rem' }}>↗</span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};
